import * as THREE from 'three';
import type { GameState, Pos } from '@color-dots/game';
import type { VisualStyle } from '../game/visualStyles.js';
import { projectOntoSphere } from './arcCurve3d.js';
import { gridToWorld } from './Grid3DLayout.js';
import { gearMetalMaterialEmissive } from './gearEmissivePolicy.js';
import { createExtrudedGearGeometry } from './gearGeometry3d.js';
import { getSceneTheme3D, type SceneTheme3D } from './sceneTheme3d.js';

export interface CellHitData {
  col: number;
  row: number;
}

const TILE_HEIGHT = 0.18;
const TILE_GAP = 0.04;
const GEAR_METALNESS = 0.7;
const GEAR_ROUGHNESS = 0.42;
const TILE_LIFT = 0.02;

export class BoardMesh {
  readonly group = new THREE.Group();
  private hitMeshes: THREE.Mesh[] = [];
  private size = 0;
  private theme: SceneTheme3D = getSceneTheme3D();
  private visualStyle: VisualStyle = 'pipe';
  private gearGeometry: THREE.ExtrudeGeometry | null = null;
  private sphereCenter = new THREE.Vector3(0, 0, 0);
  private sphereRadius = 0;

  setVisualStyle(style: VisualStyle): void {
    this.visualStyle = style;
  }

  /** Sphere center/radius (board-local space) used to seat tiles on the glass sphere's surface. */
  setSphereBounds(center: THREE.Vector3, radius: number): void {
    this.sphereCenter.copy(center);
    this.sphereRadius = radius;
  }

  getVisualStyle(): VisualStyle {
    return this.visualStyle;
  }

  applyTheme(theme: SceneTheme3D = getSceneTheme3D(this.visualStyle)): void {
    this.theme = theme;
    if (this.size === 0) return;

    let idx = 0;
    const n = this.size;
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const tile = this.group.children[idx * 2] as THREE.Mesh;
        const mat = tile.material as THREE.MeshStandardMaterial;
        const center = gridToWorld(col, row, n);
        const shade = this.getHeightShade(center.y);
        const filled = mat.userData.filled as boolean;
        this.applyTileMaterial(mat, filled, shade);
        idx++;
      }
    }
  }

  build(state: GameState): void {
    this.dispose();
    this.theme = getSceneTheme3D(this.visualStyle);
    this.size = state.size;
    const n = state.size;
    const cellSize = 1;
    const isGear = this.visualStyle === 'gear';
    if (isGear) {
      this.gearGeometry = createExtrudedGearGeometry();
    }

    const onSphere = this.sphereRadius > 1e-6;

    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const center = gridToWorld(col, row, n);
        const filled = state.grid[row][col] !== null;
        const shade = this.getHeightShade(center.y);

        const surface = onSphere
          ? projectOntoSphere(center, this.sphereCenter, this.sphereRadius, TILE_LIFT)
          : center;
        const normal = onSphere ? surface.clone().sub(this.sphereCenter).normalize() : null;
        const quat = normal
          ? new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal)
          : null;

        let tile: THREE.Mesh;
        if (isGear && this.gearGeometry) {
          const tileMat = new THREE.MeshStandardMaterial({
            color: this.getTileColor(filled, shade),
            roughness: GEAR_ROUGHNESS,
            metalness: GEAR_METALNESS,
            ...gearMetalMaterialEmissive(),
          });
          tileMat.userData.filled = filled;
          tile = new THREE.Mesh(this.gearGeometry, tileMat);
          if (normal && quat) {
            tile.position.copy(surface);
            tile.quaternion.copy(quat);
          } else {
            tile.position.set(center.x, center.y, center.z);
          }
          tile.castShadow = true;
          tile.receiveShadow = true;
        } else {
          const tileGeo = new THREE.BoxGeometry(
            cellSize - TILE_GAP,
            TILE_HEIGHT,
            cellSize - TILE_GAP,
          );
          const tileMat = new THREE.MeshStandardMaterial({
            color: this.getTileColor(filled, shade),
            roughness: 0.65,
            metalness: 0.08,
          });
          tileMat.userData.filled = filled;
          tile = new THREE.Mesh(tileGeo, tileMat);
          if (normal && quat) {
            tile.position.copy(surface).addScaledVector(normal, -TILE_HEIGHT / 2);
            tile.quaternion.copy(quat);
          } else {
            tile.position.set(center.x, center.y - TILE_HEIGHT / 2, center.z);
          }
          tile.receiveShadow = true;
        }
        this.group.add(tile);

        const hitScale = 1.1;
        const hitGeo = new THREE.BoxGeometry(
          (cellSize - TILE_GAP) * hitScale,
          TILE_HEIGHT * 2,
          (cellSize - TILE_GAP) * hitScale,
        );
        const hitMat = new THREE.MeshBasicMaterial({ visible: false });
        const hitMesh = new THREE.Mesh(hitGeo, hitMat);
        hitMesh.position.set(center.x, center.y, center.z);
        hitMesh.userData = { col, row } satisfies CellHitData;
        this.group.add(hitMesh);
        this.hitMeshes.push(hitMesh);
      }
    }
  }

  updateCells(state: GameState): void {
    let idx = 0;
    const n = state.size;
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const tile = this.group.children[idx * 2] as THREE.Mesh;
        const filled = state.grid[row][col] !== null;
        const mat = tile.material as THREE.MeshStandardMaterial;
        const center = gridToWorld(col, row, n);
        const shade = this.getHeightShade(center.y);
        mat.userData.filled = filled;
        this.applyTileMaterial(mat, filled, shade);
        idx++;
      }
    }
  }

  private applyTileMaterial(
    mat: THREE.MeshStandardMaterial,
    filled: boolean,
    shade: number,
  ): void {
    mat.color.setHex(this.getTileColor(filled, shade));
    if (this.visualStyle === 'gear') {
      mat.metalness = GEAR_METALNESS;
      mat.roughness = GEAR_ROUGHNESS;
    }
  }

  private getTileColor(filled: boolean, shade: number): number {
    const base = filled ? this.theme.tileFilled : this.theme.tileEmpty;
    return base + shade;
  }

  private getHeightShade(y: number): number {
    const shades = this.theme.tileShades;
    if (y < -0.4) return shades[0];
    if (y < 0.2) return shades[1];
    if (y < 0.8) return shades[2];
    return shades[3];
  }

  getHitMeshes(): THREE.Mesh[] {
    return this.hitMeshes;
  }

  getBoardExtent(): number {
    return this.size;
  }

  posToWorld(pos: Pos): THREE.Vector3 {
    const world = gridToWorld(pos[0], pos[1], this.size);
    world.y += TILE_HEIGHT / 2;
    return world;
  }

  dispose(): void {
    for (const child of [...this.group.children]) {
      const mesh = child as THREE.Mesh;
      if (this.visualStyle !== 'gear') {
        mesh.geometry?.dispose();
      }
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m) => m.dispose());
      } else {
        mesh.material?.dispose();
      }
      this.group.remove(mesh);
    }
    this.hitMeshes = [];
  }
}
